import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Card, Descriptions, Layout, Space, Table, TablePaginationConfig } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { API } from '../../api';
import { PagedResultState } from '../../common/CommonResult';

export const ForeignTransportDetail = () => {
  const { manifestNumber } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [transport, setTransport] = useState<any>(null);
  const [transportLoading, setTransportLoading] = useState(false);
  const [packages, setPackages] = useState<PagedResultState<any>>({
    loading: true,
    result: {},
  });

  useEffect(() => {
    if (!manifestNumber) return;
    fetchTransport(manifestNumber);
  }, [manifestNumber]);

  useEffect(() => {
    if (!manifestNumber) return;
    const page = searchParams.get('page') || '1';
    const size = searchParams.get('size') || '10';
    fetchPackages(manifestNumber, page, size);
  }, [manifestNumber, searchParams]);

  const fetchTransport = async (manifest: string) => {
    try {
      setTransportLoading(true);
      const response: any = await API.get({ apiVersion: 'api' })(
        `/foreign/transport/list?page=1&size=1&query=${encodeURIComponent(manifest)}`
      );
      const match = (response.result?.data || []).find((it: any) => it.manifestNumber === manifest);
      setTransport(match || response.result?.data?.[0] || null);
    } catch (e) {
      console.log(e);
    } finally {
      setTransportLoading(false);
    }
  };

  const fetchPackages = async (manifest: string, page: string, size: string) => {
    try {
      setPackages({ loading: true, result: {} });
      const response: any = await API.get({ apiVersion: 'api' })(
        `/foreign/package/list?manifestNumber=${encodeURIComponent(manifest)}&page=${page}&size=${size}`
      );
      setPackages({ loading: false, result: response.result });
    } catch (e) {
      console.log(e);
      setPackages({ loading: false, error: true, result: {} });
    }
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(pagination.current || 1));
    params.set('size', String(pagination.pageSize || 10));
    setSearchParams(params);
  };

  const columns = [
    {
      title: 'Богц №',
      dataIndex: 'packageNumber',
      render: (row: any, data: any) => <Link to={`/foreign/package/detail/${data.no}`}>{row}</Link>,
      key: 'packageNumber',
    },
    {
      title: 'Харилцагч',
      dataIndex: 'customer',
      render: (data: any) => data?.name,
      key: 'customerName',
    },
    {
      title: 'Жин',
      dataIndex: 'weight',
      key: 'weight',
    },
    {
      title: 'Илгээмж',
      dataIndex: 'shipmentCount',
      key: 'shipmentCount',
    },
    {
      title: 'Огноо',
      dataIndex: 'createdAt',
      render: (row: any) => (row ? dayjs(row).format('YYYY-MM-DD HH:mm:ss') : '-'),
      key: 'createdAt',
    },
    {
      title: 'Төлөв',
      render: (data: any) => <span>{data.customs !== null ? 'Гааль' : 'Үүссэн'}</span>,
      key: 'status',
    },
  ];

  return (
    <Layout.Content style={{ margin: '24px 16px 0' }}>
      <Card
        title={`Тээврийн дэлгэрэнгүй - ${manifestNumber}`}
        extra={
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/foreign/transports?page=1')}
          >
            Буцах
          </Button>
        }
      >
        <Descriptions
          bordered
          size="small"
          column={2}
          style={{ marginBottom: 16 }}
          // loading={transportLoading}
        >
          <Descriptions.Item label="Тээвэр №">
            {transport?.manifestNumber || manifestNumber}
          </Descriptions.Item>
          <Descriptions.Item label="Харилцагч">
            {transport?.customer?.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Богцны тоо">{transport?.packageCount ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Илгээмжийн тоо">
            {transport?.parcelCount ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Үүсгэсэн огноо">
            {transport?.createdAt ? dayjs(transport.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
          </Descriptions.Item>
        </Descriptions>

        <Space style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Богцнууд</h3>
        </Space>
        <Table
          pagination={{
            pageSize: packages.result.size,
            current: packages.result.page,
            total: packages.result.total,
          }}
          onChange={handleTableChange}
          loading={packages.loading}
          dataSource={packages.result.data}
          columns={columns}
          rowKey="no"
        />
      </Card>
    </Layout.Content>
  );
};

export default ForeignTransportDetail;
